// SPDX-FileCopyrightText: 2025 Ovation S.r.l. <help@dynamic.ooo>
// SPDX-License-Identifier: GPL-3.0-or-later

import { BaseField } from "./base";
import {
	parsePhoneNumberFromString,
	getCountries,
	getCountryCallingCode,
	getExampleNumber,
	AsYouType,
} from "libphonenumber-js";
import examples from "libphonenumber-js/examples.mobile.json";

export class InternationalPhoneField extends BaseField {
	constructor(field, form) {
		super(field, form);
		this.options = JSON.parse(this.field.dataset.options);
		this.wrapperElement = null;
		this.countrySelectElement = null;
		this.phoneInputElement = null;
		this.countries = getCountries();

		// Ensure defaults for label mode (back-compat with prior checkbox and removed options)
		if (!this.options.prefixLabelMode) {
			if (typeof this.options.showCountryCode !== "undefined") {
				this.options.prefixLabelMode = this.options.showCountryCode
					? "code_prefix"
					: "name_prefix";
			} else {
				this.options.prefixLabelMode = "name_prefix";
			}
		}

		// Map removed 'prefix_only' to a supported mode for robustness
		if (this.options.prefixLabelMode === "prefix_only") {
			this.options.prefixLabelMode = "code_prefix";
		}
	}

	init() {
		// Create wrapper and replace the original input
		this.createPhoneInput();
		this.setupEventListeners();
	}

	createPhoneInput() {
		this.wrapperElement = document.createElement("div");
		this.wrapperElement.className = "dtb-international-phone-wrapper";

		this.countrySelectElement = document.createElement("select");
		this.countrySelectElement.className = "dtb-international-phone-country";

		// Apply custom width to the prefix select if provided
		if (
			this.options.prefixWidth &&
			String(this.options.prefixWidth).trim() !== ""
		) {
			let width = String(this.options.prefixWidth).trim();
			// Normalize common spacing like "10 px" -> "10px"
			width = width.replace(/\s+(?=[a-zA-Z%]+)/g, "");
			if (/^\d+$/.test(width)) {
				// If only a number is provided, assume pixels
				width = `${width}px`;
			}

			// Override default CSS constraints to allow full control
			this.countrySelectElement.style.boxSizing = "border-box";
			this.countrySelectElement.style.flex = "0 0 auto";
			this.countrySelectElement.style.setProperty(
				"min-width",
				"0",
				"important",
			);
			this.countrySelectElement.style.setProperty(
				"max-width",
				"none",
				"important",
			);
			this.countrySelectElement.style.setProperty(
				"width",
				width,
				"important",
			);
		}

		this.populateCountryDropdown();

		this.phoneInputElement = document.createElement("input");
		this.phoneInputElement.type = "tel";
		this.phoneInputElement.className = "dtb-international-phone-number";
		this.phoneInputElement.style.flex = "1";

		// Set default values
		if (
			this.options.defaultCountry &&
			this.countries.includes(this.options.defaultCountry)
		) {
			this.countrySelectElement.value = this.options.defaultCountry;
		} else {
			// Default to US if the provided country code is invalid
			this.countrySelectElement.value = "US";
		}

		// Set placeholder with example number
		this.updatePlaceholder();

		// Append elements to wrapper
		this.wrapperElement.appendChild(this.countrySelectElement);
		this.wrapperElement.appendChild(this.phoneInputElement);

		// Replace original input with wrapper
		const parent = this.field.parentNode;
		parent.insertBefore(this.wrapperElement, this.field);

		// Hide original input but keep it for form submission
		this.field.style.display = "none";

		// Set initial value if field already has a value
		if (this.field.value) {
			this.parseInitialValue(this.field.value);
		}
	}

	setupEventListeners() {
		// Update hidden field when either country or number changes
		this.countrySelectElement.addEventListener("change", () => {
			this.updatePlaceholder();
			this.updateValue();
		});

		this.phoneInputElement.addEventListener("input", (e) => {
			// Format number as user types
			const formatter = new AsYouType(this.countrySelectElement.value);
			const formattedValue = formatter.input(e.target.value);

			// Only update if the formatting doesn't remove what the user is typing
			// This prevents the cursor from jumping around
			if (
				formattedValue.length >= e.target.value.length ||
				e.inputType === "deleteContentBackward"
			) {
				e.target.value = formattedValue;
			}

			this.updateValue();
		});
	}

	updatePlaceholder() {
		const countryCode = this.countrySelectElement.value;
		try {
			const exampleNumber = getExampleNumber(countryCode, examples);
			if (exampleNumber) {
				// Use national format for the placeholder
				this.phoneInputElement.placeholder =
					exampleNumber.formatNational();
			}
		} catch (error) {
			// If there's an error, use a generic placeholder
			this.phoneInputElement.placeholder = "Phone number";
		}
	}

	updateValue() {
		const countryCode = this.countrySelectElement.value;
		const phoneNumber = this.phoneInputElement.value.trim();

		if (phoneNumber) {
			try {
				// Try to parse the number with libphonenumber-js
				const parsedNumber = parsePhoneNumberFromString(
					phoneNumber,
					countryCode,
				);

				if (parsedNumber) {
					// Store in E.164 format (international format without formatting)
					this.field.value = parsedNumber.format("E.164");
				} else {
					// If parsing fails but we have a number, store with country code
					const dialCode = getCountryCallingCode(countryCode);
					this.field.value = `+${dialCode}${phoneNumber.replace(/\D/g, "")}`;
				}

				// Validate if enabled
				if (this.options.validation) {
					this.validatePhoneNumber();
				}
			} catch (error) {
				// Fallback if parsing fails
				this.field.value = phoneNumber;
			}
		} else {
			this.field.value = "";
		}
	}

	parseInitialValue(value) {
		try {
			// Try to parse the initial value with libphonenumber-js
			const parsedNumber = parsePhoneNumberFromString(value);

			if (parsedNumber) {
				// Set the country select to the detected country
				if (parsedNumber.country) {
					this.countrySelectElement.value = parsedNumber.country;
				}

				// Format the number in national format for the input
				this.phoneInputElement.value = parsedNumber.formatNational();
				return;
			}
		} catch (error) {
			// Fallback if parsing fails
		}

		// If parsing fails or no valid number:
		// Check if value starts with a plus sign (international format)
		if (value.startsWith("+")) {
			// Just put the whole number without the plus
			this.phoneInputElement.value = value.substring(1);
		} else {
			// National format - just put number in the input
			this.phoneInputElement.value = value;
		}
	}

	validatePhoneNumber() {
		const countryCode = this.countrySelectElement.value;
		const phoneNumber = this.phoneInputElement.value.trim();

		if (!phoneNumber) {
			this.phoneInputElement.setCustomValidity("");
			return;
		}

		try {
			// Use libphonenumber to validate
			const parsedNumber = parsePhoneNumberFromString(
				phoneNumber,
				countryCode,
			);

			if (!parsedNumber) {
				this.phoneInputElement.setCustomValidity(
					"Invalid phone number format",
				);
			} else if (!parsedNumber.isValid()) {
				this.phoneInputElement.setCustomValidity(
					"This phone number appears to be invalid",
				);
			} else {
				this.phoneInputElement.setCustomValidity("");
			}
		} catch (error) {
			// If validation fails for any reason, mark as invalid
			this.phoneInputElement.setCustomValidity("Invalid phone number");
		}
	}

	populateCountryDropdown() {
		// Sort countries alphabetically by the label we will display
		const getLabel = (code) => this.getOptionLabel(code);
		const sortedCountries = this.countries.sort((a, b) => {
			const labelA = getLabel(a);
			const labelB = getLabel(b);
			return labelA.localeCompare(labelB);
		});

		// Add all countries
		sortedCountries.forEach((countryCode) => {
			const option = document.createElement("option");
			option.value = countryCode;

			try {
				option.text = this.getOptionLabel(countryCode);
			} catch (error) {
				option.text = countryCode;
			}

			this.countrySelectElement.appendChild(option);
		});
	}

	getCountryName(countryCode) {
		try {
			// Get localized country name if available
			return new Intl.DisplayNames(["en"], { type: "region" }).of(
				countryCode,
			);
		} catch (error) {
			// Fallback to country code if name is not available
			return countryCode;
		}
	}

	getOptionLabel(countryCode) {
		const mode = this.options.prefixLabelMode;
		let name;
		let dialCode;
		try {
			name = this.getCountryName(countryCode);
			// getCountryCallingCode can throw for unknown/malformed codes
			dialCode = getCountryCallingCode(countryCode);
		} catch (e) {
			name = countryCode;
			dialCode = null;
		}

		switch (mode) {
			case "code_prefix":
				return dialCode ? `${countryCode} (+${dialCode})` : countryCode;
			case "name_only":
				return name;
			case "code_only":
				return countryCode;
			case "prefix_name":
				return dialCode ? `+${dialCode} — ${name}` : name;
			case "name_prefix":
			default:
				return dialCode ? `${name} (+${dialCode})` : name;
		}
	}
}
